import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { ThumbsUp as ThumbsUpIcon } from '@factorialco/f0-react/icons/app'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NodeUIApproval } from 'modules/workflows/components/Node/types'
import { ApprovalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/approvalFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

import { BasicInputs } from './partials/BasicInputs'
import { SidepanelFooter } from './partials/SidepanelFooter'
import { SidepanelHeader } from './partials/SidepanelHeader'
import { SidepanelWrapper } from './partials/SidepanelWrapper'

type Props = {
  node: NodeUIApproval
}
export const ApprovalNodeSidepanel = ({ node }: Props) => {
  const { workflowManifest, handlersConfig, setNodesUI } = useWorkflowContext()

  const { handleSubmit, getValues, control, watch, setValue } = useFormContext<ApprovalFormSchema>()

  const handlerName = watch('nodeData.handlerType')
  const handlerForm = handlersConfig[handlerName]
  const HandlerFormComponent = handlerForm?.component

  useUnsafeEffect(() => {
    //When we change the handler type the form payload must be reset to the default value
    const currentHandlerForm = handlersConfig[handlerName]
    const payloadData = currentHandlerForm?.initializer?.(node.data.payload)

    setValue('nodeData.payload', payloadData || {})
  }, [handlerName])

  const approvalTypes = workflowManifest.handlers
    .filter((handler) => handler.actionGroup === 'approvals')
    .map((type) => ({
      value: type.name,
      label: handlersConfig[type.name]?.dropDownLabel || type.name,
    }))

  // Closes form by deselecting all nodes
  const handleCancel = () => {
    setNodesUI((prev) => prev.map((n) => ({ ...n, selected: false })))
  }

  // Updates global state and deselects all nodes
  const handleSave = () => {
    const { title, description, nodeData } = getValues()

    const deriveApprovalAutoTitle = (handlerName: string): string => {
      const normalized = (handlerName || '').toLowerCase()
      if (normalized.includes('reject')) {
        return i18n.t('workflows.approval.types.auto_reject')
      } else if (normalized.includes('approve')) {
        return i18n.t('workflows.approval.types.auto_approve')
      } else {
        return i18n.t('workflows.approval.types.request_review')
      }
    }

    // If the title or description is empty, set the default title or description
    // Otherwise, assume it was already set and keep it as is
    const shouldSetDefaultTitle =
      (title ?? '').trim() === '' ||
      title === i18n.t('workflows.node_type.approval.default.title') ||
      title === i18n.t('workflows.approval.types.auto_approve') ||
      title === i18n.t('workflows.approval.types.auto_reject') ||
      title === i18n.t('workflows.approval.types.request_review')
    const shouldSetDefaultDescription = (description ?? '').trim() === ''

    const beforeSaveData = handlerForm?.beforeSave

    setNodesUI((prev) =>
      prev.map((n) => {
        if (n.id === node.id) {
          return {
            ...node,
            data: {
              ...node.data,
              title: shouldSetDefaultTitle ? deriveApprovalAutoTitle(handlerName) : title,
              description: shouldSetDefaultDescription
                ? i18n.t('workflows.building_blocks.approval.description')
                : description,
              handler_name: nodeData.handlerType,
              payload: beforeSaveData
                ? beforeSaveData(nodeData.payload, nodeData.handlerType)
                : nodeData.payload,
            },
            selected: false,
          }
        }
        return { ...n, selected: false }
      })
    )
  }

  const onSubmit = () => {
    handleSubmit(handleSave, (_fields) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('workflows.sidepanel.approval.validation.generic_error'),
        time: 3000,
      })
    })()
  }

  return (
    <SidepanelWrapper
      footer={<SidepanelFooter onCancel={handleCancel} onSave={onSubmit} isValid />}
    >
      <SidepanelHeader
        nodeId={node.id}
        backgroundColor="#6156ED20"
        icon={
          <ThumbsUpIcon width={22} height={22} style={{ color: '#6156ED', strokeWidth: 1.5 }} />
        }
        title={i18n.t('workflows.sidepanel.approval.edit_title')}
      />

      <BasicInputs
        titleLabel={i18n.t('workflows.sidepanel.task.block_title_label')}
        titlePlaceholder={i18n.t('workflows.sidepanel.task.block_title_placeholder')}
        descriptionLabel={i18n.t('workflows.sidepanel.task.block_description_label')}
        descriptionPlaceholder={i18n.t('workflows.sidepanel.task.block_description_placeholder')}
      />

      <Box gap="s16">
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('workflows.sidepanel.approval.title')}
        </Text>

        <Controller
          name="nodeData.handlerType"
          control={control}
          render={({ field, fieldState }) => (
            <F0Select
              label={i18n.t('workflows.sidepanel.general.fields.type')}
              value={field.value}
              onChange={field.onChange}
              options={approvalTypes}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {HandlerFormComponent !== undefined && (
        <Box gap="s16">
          <HandlerFormComponent payload={{}} onChange={() => {}} />
        </Box>
      )}
    </SidepanelWrapper>
  )
}
